import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useScrollSelectedOnOpen from '../../../hooks/useScrollSelectedOnOpen';
import {
  FILTER_CHEVRON_CLASS,
  FILTER_CHEVRON_SIZE,
  FILTER_VALUE_CLASS,
  FILTER_VALUE_TEXT_CLASS,
  filterTriggerClass,
} from '../filterControlStyles';

/**
 * Searchable single-select for map importer/exporter. Stacked strip chrome
 * (same 32px trigger as Status). options: string[] from server.
 * value '' = All.
 */
const PartySelect = ({
  label,
  value = '',
  options = [],
  onChange,
  allLabel,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  useScrollSelectedOnOpen(open, listRef);
  const allText = allLabel || t('map.filters.all', 'All');

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery('');
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((name) => name.toLowerCase().includes(q));
  }, [options, query]);

  const display = value ? `${label}: ${value}` : `${label}: ${allText}`;

  return (
    <div className="relative min-w-[160px] max-w-[220px]" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className={`w-full gap-[8px] ${filterTriggerClass({ open })}`}
      >
        <span className={`${FILTER_VALUE_CLASS} ${FILTER_VALUE_TEXT_CLASS}`} title={display}>
          {display}
        </span>
        <ChevronDown
          size={FILTER_CHEVRON_SIZE}
          className={`${FILTER_CHEVRON_CLASS} ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-[2100] mt-1 flex max-h-[320px] w-[260px] flex-col overflow-hidden rounded-lg border border-[#C3C4C3] bg-white shadow-lg dark:border-[#828382] dark:bg-[#191919]">
          <div className="shrink-0 border-b border-[#E8EBE9] p-2 dark:border-[#3a3c3b]">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#828382]" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('map.filters.search', 'Search...')}
                className="w-full rounded border border-[#C3C4C3] bg-[#F0F5F2] py-1.5 pl-8 pr-2 text-xs text-[#282A29] focus:outline-none focus:ring-1 focus:ring-bloom-green dark:border-[#828382] dark:bg-[#232121] dark:text-[#f0f5f2]"
              />
            </div>
          </div>
          <div ref={listRef} className="overflow-y-auto py-1">
            <button
              type="button"
              role="option"
              aria-selected={!value}
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className={`flex min-h-9 w-full items-center px-3 py-1.5 text-left text-xs ${
                !value
                  ? 'bg-bloom-green/10 font-semibold text-bloom-green'
                  : 'text-[#282A29] hover:bg-[#F0F5F2] dark:text-[#f0f5f2] dark:hover:bg-[#232121]'
              }`}
            >
              {allText}
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-center text-xs text-[#828382]">
                {t('map.filters.noMatches', 'No matches')}
              </div>
            ) : (
              filtered.map((name) => (
                <button
                  key={name}
                  type="button"
                  role="option"
                  aria-selected={value === name}
                  title={name}
                  onClick={() => {
                    onChange(name);
                    setOpen(false);
                  }}
                  className={`flex min-h-9 w-full items-center px-3 py-1.5 text-left text-xs ${
                    value === name
                      ? 'bg-bloom-green/10 font-semibold text-bloom-green'
                      : 'text-[#282A29] hover:bg-[#F0F5F2] dark:text-[#f0f5f2] dark:hover:bg-[#232121]'
                  }`}
                >
                  <span className="truncate">{name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PartySelect;
